/**
 * Tenant-area route registration.
 *
 * `.ts`, not `.tsx`: `../routes/index.ts` discovers this file by a literal
 * glob over every feature area's `routes.ts`, so the extension is
 * load-bearing and every element below is built with `createElement` rather
 * than JSX.
 *
 * `/convite` mounts outside the shell (`outsideShell: true`, on
 * `ACCEPT_ROUTE`): the invitee may hold no account at all (§18.2), so it
 * cannot sit behind the shell's sign-in wall. `/comecar` mounts outside the
 * shell too: a membership-less caller has no organisation for the rail, the
 * switcher or the account footer to point at, and Figma's `128:426`,
 * `148:1532` and `354:13640` all draw a bare page around the card. Every
 * other route here is gated by the capability the API derived, never by a
 * role read on the client (§3, §17.2).
 *
 * `CAPACITY_ROUTES`/`CAPACITY_NAV` (`./capacity/routes.ts`) fold in the
 * `Créditos e vagas` screen that now mounts at `/creditos`; that file's own
 * doc comment says why it cannot register itself.
 */
import { createElement } from 'react';
import { Megaphone, ShieldCheck, Tag, Users } from 'lucide-react';
import { Navigate, useParams } from '@tanstack/react-router';

import type { NavEntry, RouteBundle, RouteModule } from '../routes/contract';
import { ACCEPT_ROUTE } from './accept/AcceptInvitation';
import { CAPACITY_NAV, CAPACITY_REDIRECTS, CAPACITY_ROUTES } from './capacity/routes';
import { DataSettings } from './data/DataSettings';
import { dt } from './data/messages';
import { TenantHome } from './home/TenantHome';
import { tt } from './i18n';
import { nt } from './i18n/nav';
import { Onboarding } from './onboarding/Onboarding';
import { ONTOLOGY_NAV, ONTOLOGY_ROUTES } from './ontology/routes';
import { Team } from './pages/Team';
import { DATA_PATH, LEGACY_TEAM_PATH, USERS_PATH } from './paths';
import { tgt } from './tags/messages';
import { Tags } from './tags/Tags';

const TAGS_PATH = '/c/$campaignId/tags';

/** Untyped for the reason `../control/routes.ts`'s own `useRouteParams` is: the
 *  router is composed at runtime, so there is no generated tree to type against. */
function TagsRoute() {
  const { campaignId } = useParams({ strict: false }) as { campaignId?: string };
  return createElement(Tags, { campaignId: campaignId ?? '' });
}

function LegacyTeamRoute() {
  const { tenantId } = useParams({ strict: false }) as { tenantId?: string };
  return createElement(Navigate, {
    to: USERS_PATH,
    params: { tenantId: tenantId ?? '' } as never,
    replace: true,
  });
}

export const TENANT_ROUTES: RouteModule[] = [
  { path: '/comecar', element: createElement(Onboarding), kind: 'form', outsideShell: true },
  { ...ACCEPT_ROUTE, outsideShell: true },
  {
    path: '/t/$tenantId',
    element: createElement(TenantHome),
    requires: 'control_plane.read',
    kind: 'data',
  },
  {
    path: USERS_PATH,
    element: createElement(Team),
    requires: 'control_plane.write',
    kind: 'data',
  },
  {
    path: LEGACY_TEAM_PATH,
    element: createElement(LegacyTeamRoute),
    requires: 'control_plane.write',
    kind: 'data',
  },
  ...CAPACITY_ROUTES,
  ...ONTOLOGY_ROUTES,
  {
    path: DATA_PATH,
    element: createElement(DataSettings),
    requires: 'control_plane.write',
    kind: 'form',
  },
  {
    path: TAGS_PATH,
    element: createElement(TagsRoute),
    requires: 'control_plane.read',
    kind: 'data',
  },
];

export const TENANT_NAV: NavEntry[] = [
  {
    path: '/t/$tenantId',
    label: () => nt('home'),
    requires: 'control_plane.read',
    icon: Megaphone,
    order: 10,
    kind: 'data',
  },
  {
    path: USERS_PATH,
    label: () => tt('nav.team'),
    requires: 'control_plane.write',
    icon: Users,
    order: 40,
    kind: 'data',
  },
  ...CAPACITY_NAV,
  ...ONTOLOGY_NAV,
  {
    path: DATA_PATH,
    label: () => dt('nav'),
    requires: 'control_plane.write',
    icon: ShieldCheck,
    order: 60,
    kind: 'form',
  },
];

/** `$campaignId` is the shell's to fill -- see `RouteBundle.campaignNav`. */
export const TENANT_CAMPAIGN_NAV: NavEntry[] = [
  {
    path: TAGS_PATH,
    label: () => tgt('heading'),
    requires: 'control_plane.read',
    icon: Tag,
    order: 110,
    kind: 'data',
  },
];

export const bundle: RouteBundle = {
  routes: TENANT_ROUTES,
  redirects: CAPACITY_REDIRECTS,
  nav: TENANT_NAV,
  campaignNav: TENANT_CAMPAIGN_NAV,
};
